import Guard from 'arg-guard'

import Casing from '@/helpers/casing'
import MathTools from '@/helpers/math-tools'

/**
 * Defined structure of Info about an Improvement.
 * ImprovementBasics is a subset of a full Improvement.
 * @export ImprovementBasics
 * @class ImprovementBasics
 */
export default class ImprovementBasics {

    /**
     * Creates an instance of ImprovementBasics.
     * @param {ImprovementBasicsAsJson|ImprovementSourceData|Object} data Source data - From API or state.
     */
    constructor(data) {
        Guard.throwIf(data, 'data')

        /** @type {number|null} **/
        this.id = parseInt(data.id || data.groupId) || null

        /** @type {string} **/
        this.title = data.title || ''

        /** @type {string} **/
        this.$key = Casing.camelCase(this.title || `improvement${ this.id }`)

        /** @type {number} **/
        this.price = data.price ? parseInt(data.price.toString()) || 0 : 0

        /** @type {string} **/
        this.priceDisplay = this.price > 0 ? MathTools.segmentCurrency(this.price) : 'Free'

        /** @type {string|null} **/
        this.description = data.description

        /** @type {string|null} **/
        this.imageUrl = data.imageUrl

        /** @type {boolean} **/
        this.hasImage = data.hasImage

        if (data.details) {
            /** @type {?string} **/
            this.details = data.details
        }
    }

    /**
     * Returns the object in a form suitable to store.
     * @memberof ImprovementBasics
     * @returns ImprovementBasicsAsJson
     */
    toStoreJson() {
        return {
            id: this.id,
            title: this.title,
            price: this.price,
            description: this.description,
            imageUrl: this.imageUrl,
            hasImage: this.hasImage,
            details: this.details,
        }
    }

    /**
     * Ensures that the provided data is already of this type. If not, it runs the constructor.
     * @static
     * @param {ImprovementBasicsAsJson|Object} data Source data - From API or state.
     * @returns {ImprovementBasics} Either the data is passed back if already of this type or the result of the constructor is.
     */
    static ensure(data) {
        return data && data instanceof ImprovementBasics ? data : new ImprovementBasics(data)
    }

    /**
     * Returns the object type from raw stored JSON data.
     * @param {?ImprovementBasicsAsJson} [data=null] The stored data.
     * @returns {ImprovementBasics}
     */
    static fromStoreJson(data = null) {
        return data ? new ImprovementBasics(data) : null
    }
}
